import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import Dropzone from 'react-dropzone';
import FilePreview from './file-preview';
import { getFileObject, IMAGE_EXTENSIONS_LIST, PDF_EXTENSIONS_LIST, VIDEO_EXTENSIONS_LIST } from 'helpers/FileHelpers';
import { DropzoneContainer, DropzoneText, ErrorMessage} from './components';
import { ChallengeApplet } from 'models/ChallengeApplet';
import { Applet } from 'models/Applet';
import localization from 'localization';
import { needsWarning as needsImageWarning, isImageFile, validateImageForUpload } from 'modules/files/image-compression';
import { isVideoFile, validateVideoForUpload } from 'modules/files/video-compression';
import { IAppletFields } from 'models/contentful';

export type UploadFile = {
  name: string;
  type: string;
  uri: string;
  mime?: string;
  originalFile?: File; // File original cuando está disponible (útil para archivos grandes)
};

export enum FileType {
  images = 'image/jpeg,image/png,image/bmp,image/tiff,image/heic,image/heif,image/webp',
  videos = 'video/mp4,video/avi,video/quicktime,video/x-m4v',
  docs = 'application/pdf,text/plain',
  audio = 'audio/*',
  all = 'image/jpeg,image/png,image/bmp,image/tiff,image/heic,image/heif,image/webp,video/mp4,video/avi,video/quicktime,video/x-m4v,application/pdf,text/plain,audio/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,application/vnd.oasis.opendocument.text',
}

/**
 * Generates a personalized error message based on the supported file types
 */
const getFileTypeErrorMessage = (supportedTypes?: IAppletFields['supportedTypes']): string => {
  if (!supportedTypes || supportedTypes.length === 0) {
    return 'Only JPG, PNG, BMP, TIF, HEIC and WEBP images, MP4, AVI, MOV and M4V videos are allowed';
  }

  const allowedTypes: string[] = [];
  if (supportedTypes.includes('images')) {
    allowedTypes.push('JPG, PNG, BMP, TIF, HEIC and WEBP images');
  }
  if (supportedTypes.includes('videos')) {
    allowedTypes.push('MP4, AVI, MOV and M4V videos');
  }
  if (supportedTypes.includes('docs')) {
    allowedTypes.push('PDF documents');
  }
  if (supportedTypes.includes('audio')) {
    allowedTypes.push('audio files');
  }

  if (allowedTypes.length === 0) {
    return 'Only JPG, PNG, BMP, TIF, HEIC and WEBP images, MP4, AVI, MOV and M4V videos are allowed';
  }

  if (allowedTypes.length === 1) {
    return `Only ${allowedTypes[0]} are allowed`;
  }

  const lastType = allowedTypes.pop();
  return `Only ${allowedTypes.join(', ')}, and ${lastType} are allowed`;
};

type AppUploadProps = {
  value?: UploadFile | Applet | ChallengeApplet;
  onChangeFile?: (value: UploadFile | undefined) => void;
  disabled?: boolean;
  previewPointerEvents?: React.CSSProperties['pointerEvents'];
  fileType?: FileType[] | FileType;
  error?: string;
  onErrorChange?: (error: string | undefined) => void;
  supportedTypes?: IAppletFields['supportedTypes'];
};

const AppUpload = ({
  value,
  onChangeFile = () => { },
  disabled,
  previewPointerEvents,
  fileType = FileType.all,
  error: externalError,
  onErrorChange,
  supportedTypes,
}: AppUploadProps) => {
  const [warningShown, setWarningShown] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  // Get personalized error message based on supportedTypes
  const errorMessage = useMemo(() => getFileTypeErrorMessage(supportedTypes), [supportedTypes]);

  // Determine allowed extensions based on supportedTypes
  const getAllowedExtensions = useCallback((): string[] => {
    if (!supportedTypes || supportedTypes.length === 0) {
      // Default: allow images and videos
      return [...IMAGE_EXTENSIONS_LIST, ...VIDEO_EXTENSIONS_LIST];
    }

    const allowed: string[] = [];
    if (supportedTypes.includes('images')) {
      allowed.push(...IMAGE_EXTENSIONS_LIST);
    }
    if (supportedTypes.includes('videos')) {
      allowed.push(...VIDEO_EXTENSIONS_LIST);
    }
    if (supportedTypes.includes('docs')) {
      allowed.push(...PDF_EXTENSIONS_LIST);
    }
    // Note: audio extensions are not in the current restricted list, so we skip them for now

    return allowed.length > 0 ? allowed : [...IMAGE_EXTENSIONS_LIST, ...VIDEO_EXTENSIONS_LIST];
  }, [supportedTypes]);

  // Helper function to validate file extension
  const validateFileExtension = useCallback((fileName: string, mimeType: string): string | undefined => {
    const lastDotIndex = fileName.toLowerCase().lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
      // No extension found
      return errorMessage;
    }

    const extension = fileName.toLowerCase().substring(lastDotIndex);
    const allowedExtensions = getAllowedExtensions();

    // Check if extension is in allowed list
    if (!allowedExtensions.includes(extension)) {
      return errorMessage;
    }

    // Additional MIME type validation for images
    if (mimeType.startsWith('image/') && !['image/jpeg', 'image/png', 'image/bmp', 'image/x-bmp', 'image/tiff', 'image/tif', 'image/heic', 'image/heif', 'image/webp'].includes(mimeType)) {
      return errorMessage;
    }

    // Additional MIME type validation for videos
    if (mimeType.startsWith('video/') && !['video/mp4', 'video/avi', 'video/quicktime', 'video/x-m4v', 'video/mov'].includes(mimeType)) {
      return errorMessage;
    }

    // Additional MIME type validation for PDFs
    if (mimeType === 'application/pdf' && !PDF_EXTENSIONS_LIST.includes(extension)) {
      return errorMessage;
    }

    return undefined;
  }, [errorMessage, getAllowedExtensions]);

  const removeFile = useCallback(() => {
    onChangeFile(undefined);
    setWarningShown(false);
    setValidationError(undefined);
    if (onErrorChange) {
      onErrorChange(undefined);
    }
  }, [onChangeFile, onErrorChange]);

  const safeValue = useMemo(() => getFileObject(value), [value]);

  const acceptedFileTypes = useMemo(
    () => (fileType instanceof Array ? fileType.join(',') : fileType),
    [fileType],
  );

  // Mostrar advertencia cuando se selecciona un archivo grande
  useEffect(() => {
    const checkFileSize = async () => {
      if (!safeValue || warningShown) return;

      try {
        // Verificar si es imagen y necesita advertencia
        if (isImageFile(safeValue)) {
          const warning = await needsImageWarning(safeValue);
          if (warning.needsWarning && warning.message) {
            toast.info(warning.message, {
              position: 'top-center',
              autoClose: 5000,
            });
            setWarningShown(true);
          }
        }
        // Nota: El mensaje de advertencia para archivos grandes se muestra durante la subida,
        // no cuando se selecciona el archivo (ver services.ts)
      } catch (error) {
        console.error('Error verificando tamaño de archivo:', error);
      }
    };

    checkFileSize();
  }, [safeValue, warningShown]);

  const handleFileDrop = useCallback(
    async (files) => {
      if (files.length > 0) {
        const droppedFile = files[0];

        // Validate file extension and MIME type
        const extensionError = validateFileExtension(droppedFile.name, droppedFile.type);
        if (extensionError) {
          setValidationError(extensionError);
          if (onErrorChange) {
            onErrorChange(extensionError);
          }
          return;
        }

        // Create file object for size validation
        const file = getFileObject({
          name: droppedFile.name,
          type: droppedFile.type,
          uri: URL.createObjectURL(droppedFile),
          originalFile: droppedFile, // Guardar el File original para archivos grandes
        });

        if (!file) {
          const sizeError = localization.formatErrorMessage;
          setValidationError(sizeError);
          if (onErrorChange) {
            onErrorChange(sizeError);
          }
          return;
        }

        // Validate file size (images: 100MB max, videos: 10,000MB max)
        try {
          if (isImageFile(file)) {
            const imageValidation = await validateImageForUpload(file);
            if (!imageValidation.valid) {
              const sizeError = localization.fileUploadExceedsMaxSize;
              setValidationError(sizeError);
              if (onErrorChange) {
                onErrorChange(sizeError);
              }
              return;
            }
          } else if (isVideoFile(file)) {
            const videoValidation = await validateVideoForUpload(file);
            if (!videoValidation.valid) {
              const sizeError = localization.fileUploadExceedsMaxSize;
              setValidationError(sizeError);
              if (onErrorChange) {
                onErrorChange(sizeError);
              }
              return;
            }
          }
        } catch (error) {
          console.error('Error validating file size:', error);
          // If validation fails, continue with upload (let backend handle it)
        }

        // Clear any previous errors
        setValidationError(undefined);
        if (onErrorChange) {
          onErrorChange(undefined);
        }

        setWarningShown(false); // Resetear advertencia para nuevo archivo
        onChangeFile(file);
      } else {
        alert(localization.formatErrorMessage);
      }
    },
    [onChangeFile, validateFileExtension, onErrorChange],
  );

  const displayError = externalError || validationError;

  return (
    <>
      {!safeValue ? (
        <>
          <Dropzone
            data-testid="drag-drop-zone"
            maxFiles={1}
            multiple={false}
            onDrop={handleFileDrop}
            accept={acceptedFileTypes}>
            {({ getRootProps, getInputProps }) => (
              <DropzoneContainer
                {...getRootProps({ 'data-testid': 'drag-drop-zone' })}
              >
                <input data-testid="drag-drop-zone-input" {...getInputProps()} />
                <DropzoneText>Drop file here or click to upload</DropzoneText>
              </DropzoneContainer>
            )}
          </Dropzone>
          {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
        </>
      ) : (
        <FilePreview
          file={safeValue}
          removeFile={removeFile}
          disabled={disabled}
          style={{ pointerEvents: previewPointerEvents }}
        />
      )}
    </>
  );
};

export default AppUpload;
